import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ChatCircle, FileText, Calendar, X, EnvelopeSimple } from '@phosphor-icons/react';
import { motion, AnimatePresence } from '@/lib/motion';

/**
 * QuickActions - Floating action sidebar for desktop, FAB for mobile
 * Provides quick access to: Get Quote, Downloads, Book Visit, Call Us
 */
export const QuickActions = () => {
  const [hoveredAction, setHoveredAction] = useState(null);

  const actions = [
    {
      id: 'quote',
      icon: EnvelopeSimple,
      label: "Get Quote",
      href: "/collaborate",
      bgColor: "bg-bronze",
      hoverBg: "hover:bg-bronze/90"
    },
    {
      id: 'downloads',
      icon: FileText,
      label: "Downloads",
      href: "/downloads",
      bgColor: "bg-obsidian",
      hoverBg: "hover:bg-obsidian/80"
    },
    {
      id: 'booking',
      icon: Calendar,
      label: "Book Visit",
      href: "/booking",
      bgColor: "bg-obsidian",
      hoverBg: "hover:bg-obsidian/80"
    },
    {
      id: 'call',
      icon: Phone,
      label: "Call Us",
      href: "tel:+97142585754",
      external: true,
      bgColor: "bg-obsidian",
      hoverBg: "hover:bg-obsidian/80"
    }
  ];

  return (
    <>
      {/* Desktop Sidebar - Right Edge */}
      <div 
        className="hidden lg:flex fixed right-0 top-1/2 -translate-y-1/2 z-40 flex-col"
        data-testid="quick-actions-sidebar"
      >
        {actions.map((action, index) => {
          const isHovered = hoveredAction === action.id;
          const Icon = action.icon;
          
          const ButtonContent = (
            <div
              className="relative group flex items-center"
              onMouseEnter={() => setHoveredAction(action.id)}
              onMouseLeave={() => setHoveredAction(null)}
            >
              {/* Label - slides out on hover */}
              <AnimatePresence>
                {isHovered && (
                  <motion.span
                    initial={{ opacity: 0, x: 10, width: 0 }}
                    animate={{ opacity: 1, x: 0, width: 'auto' }}
                    exit={{ opacity: 0, x: 10, width: 0 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-full mr-0 px-4 py-2 bg-obsidian text-white text-xs font-semibold uppercase tracking-wider whitespace-nowrap overflow-hidden"
                  >
                    {action.label}
                  </motion.span>
                )}
              </AnimatePresence>
              
              {/* Icon Button */}
              <div 
                className={`w-12 h-12 ${action.bgColor} ${action.hoverBg} text-white flex items-center justify-center transition-all duration-200 ${
                  index === 0 ? 'rounded-tl-sm' : ''
                } ${index === actions.length - 1 ? 'rounded-bl-sm' : ''}`}
              >
                <Icon weight="fill" className="w-5 h-5" />
              </div>
            </div>
          );

          return action.external ? (
            <a 
              key={action.id} 
              href={action.href}
              aria-label={action.label}
              data-testid={`quick-action-${action.id}`}
            >
              {ButtonContent}
            </a>
          ) : (
            <Link 
              key={action.id} 
              to={action.href}
              aria-label={action.label}
              data-testid={`quick-action-${action.id}`}
            >
              {ButtonContent}
            </Link>
          );
        })}
      </div>

      {/* Mobile FAB - Bottom Right */}
      <MobileFAB actions={actions} />
    </>
  );
};

/**
 * Mobile FAB Component
 */
const MobileFAB = ({ actions }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="fixed bottom-[248px] sm:bottom-52 right-4 z-50 lg:hidden hidden sm:block">
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-14 right-0 flex flex-col gap-2 items-end mb-2"
          >
            {actions.map((action, index) => {
              const Icon = action.icon;
              const Content = (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-center gap-2 px-4 py-2.5 ${action.bgColor} text-white rounded-full shadow-lg`}
                >
                  <Icon weight="fill" className="w-4 h-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">{action.label}</span>
                </motion.div>
              );

              return action.external ? (
                <a key={action.id} href={action.href}>{Content}</a>
              ) : (
                <Link key={action.id} to={action.href} onClick={() => setIsExpanded(false)}>{Content}</Link>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-12 h-12 rounded-full shadow-xl flex items-center justify-center transition-all ${
          isExpanded ? 'bg-obsidian rotate-45' : 'bg-bronze'
        } text-white`}
        aria-label="Quick actions menu"
        data-testid="quick-actions-fab-toggle"
      >
        {isExpanded ? (
          <X weight="bold" className="w-5 h-5" />
        ) : (
          <ChatCircle weight="fill" className="w-5 h-5" />
        )}
      </button>
    </div>
  );
};

export default QuickActions;
